import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyOrders, addRating } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_STEPS = ['pending', 'accepted', 'picked_up', 'in_progress', 'delivered'];
const STATUS_LABELS = {
  pending: 'Waiting for shop',
  accepted: 'Shop accepted',
  picked_up: 'Clothes picked up',
  in_progress: 'Being cleaned',
  delivered: 'Delivered ✓',
  cancelled: 'Cancelled'
};

function OrderCard({ order, onRated }) {
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [ratingLoading, setRatingLoading] = useState(false);

  const statusIdx = STATUS_STEPS.indexOf(order.status);
  const badgeClass = `badge badge-${order.status?.replace('_', '-')}`;

  const handleRate = async () => {
    setRatingLoading(true);
    try {
      await addRating({ shop_id: order.accepted_by_shop, order_id: order.id, rating, review });
      setShowRating(false);
      onRated?.();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to submit rating');
    } finally { setRatingLoading(false); }
  };

  return (
    <div className="card order-card">
      <div style={{ padding: '1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>
            Order #{order.id?.slice(-8)}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: 2 }}>
            {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
        </div>
        <span className={badgeClass}>{order.status?.replace('_', ' ')}</span>
      </div>

      <div style={{ padding: '1rem 1.25rem' }}>
        <div className="order-meta">
          <span>👕 {order.clothes_count} items</span>
          <span style={{ color: 'var(--text-muted)' }}>·</span>
          <span>📍 {order.customer_address?.slice(0, 40)}...</span>
        </div>
        <div className="order-services">
          {order.services?.map(s => (
            <span key={s} className="order-service-tag">{s.replace('_', ' ')}</span>
          ))}
        </div>

        {order.shop && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'var(--primary-light)', borderRadius: 8, fontSize: '0.85rem', color: 'var(--primary-dark)' }}>
            🏪 <strong>{order.shop.name}</strong> · 📞 {order.shop.phone}
          </div>
        )}

        {/* Progress bar */}
        {order.status !== 'cancelled' && statusIdx >= 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {STATUS_STEPS.slice(0, 5).map((s, i) => (
                <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= statusIdx ? 'var(--primary)' : 'var(--border)', transition: 'background 0.3s' }} />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Placed</span>
              <span style={{ fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 600 }}>{STATUS_LABELS[order.status]}</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Done</span>
            </div>
          </div>
        )}

        {/* Rating button */}
        {order.status === 'delivered' && order.accepted_by_shop && !showRating && (
          <button className="btn btn-outline btn-sm" style={{ marginTop: 12 }} onClick={() => setShowRating(true)}>
            ⭐ Rate this service
          </button>
        )}

        {showRating && (
          <div style={{ marginTop: 12, padding: 12, background: 'var(--bg)', borderRadius: 10 }}>
            <div className="stars" style={{ justifyContent: 'center', marginBottom: 10 }}>
              {[1, 2, 3, 4, 5].map(n => (
                <span key={n} className={`star ${n <= rating ? 'filled' : ''}`}
                  onClick={() => setRating(n)}>★</span>
              ))}
            </div>
            <textarea className="form-control" placeholder="Write a review..." style={{ marginBottom: 8 }}
              value={review} onChange={e => setReview(e.target.value)} rows={2} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleRate} disabled={!rating || ratingLoading}>
                Submit
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowRating(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomerDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { user } = useAuth();

  const loadOrders = async () => {
    try {
      const res = await getMyOrders();
      setOrders(res.data);
    } catch (e) {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadOrders(); }, []);

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>My Orders</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
            👋 Hello, {user?.name || 'Customer'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link to="/customer/new-order" className="btn btn-primary">+ New Order</Link>
          <Link to="/shops" className="btn btn-outline">Browse Shops</Link>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {['all', 'pending', 'accepted', 'in_progress', 'delivered'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-ghost'}`}>
            {f === 'all' ? 'All' : f.replace('_', ' ')}
          </button>
        ))}
      </div>

      {loading && <div className="spinner" />}
      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>🧺</div>
          <p>No orders yet.</p>
          <Link to="/customer/new-order" className="btn btn-primary" style={{ marginTop: 16 }}>Place Your First Order</Link>
        </div>
      )}
      <div className="grid-auto">
        {filtered.map(order => (
          <OrderCard key={order.id} order={order} onRated={loadOrders} />
        ))}
      </div>
    </div>
  );
}
